import { z } from 'zod';

const getDocumentRequests = step => {
  if (Array.isArray(step.documents)) return step.documents;
  if (step.documentName) {
    return [
      {
        documentName: step.documentName,
        documentType: step.documentType || '',
        includeDescription: step.includeDescription || false,
        descriptionRequired: step.descriptionRequired || false,
        includeExpirationDate: step.includeExpirationDate || false,
        expirationDateRequired: step.expirationDateRequired || false,
        documentRequired: step.documentRequired || false,
      },
    ];
  }
  return [];
};

export const generateDynamicSchema = (flowData, t) => {
  let schemaObj = {};

  if (!flowData || !flowData.steps) return z.object({});

  flowData.steps.forEach(step => {
    if (step.fields) {
      Object.entries(step.fields).forEach(([fieldId, config]) => {
        const isReq = config.required;

        if (fieldId === 'birthday') {
          schemaObj[fieldId] = z.any().superRefine((val, ctx) => {
            if (!val) {
              if (isReq) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.birthdayRequired', 'Birthday is required') });
              }
              return;
            }

            const day = val?.day;
            const month = val?.month;
            const year = val?.year;

            const hasAny = !!day || !!month || !!year;

            if (!hasAny) {
              if (isReq) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.birthdayRequired', 'Birthday is required') });
              }
              return;
            }

            if (!day || !month || !year) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.completeBirthdayRequired', 'Complete birthday is required') });
              return;
            }

            const d = Number(day);
            const m = Number(month);
            const y = Number(year);

            if (isNaN(d) || d < 1 || d > 31) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.invalidDay', 'Invalid day') });
              return;
            }
            if (isNaN(m) || m < 1 || m > 12) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.invalidMonth', 'Invalid month') });
              return;
            }
            if (isNaN(y) || y < 1900 || y > new Date().getFullYear()) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.invalidYear', 'Invalid year') });
              return;
            }

            const date = new Date(y, m - 1, d);
            if (isNaN(date.getTime()) || date.getFullYear() !== y || date.getMonth() + 1 !== m || date.getDate() !== d) {
              ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.invalidDate', 'Invalid date') });
            }
          });

          return;
        }

        let fieldSchema = z.string({
          required_error: t('INVITE_FLOW.fieldRequired', 'This field is required'),
          invalid_type_error: t('INVITE_FLOW.fieldRequired', 'This field is required'),
        });

        if (fieldId === 'codiceFiscale') {
          fieldSchema = fieldSchema
            .length(16, t('INVITE_FLOW.codiceFiscaleLength', 'Codice Fiscale must be 16 characters'))
            .regex(/^[A-Z0-9]+$/i, t('INVITE_FLOW.alphanumericOnly', 'Alphanumeric only'));
        } else if (fieldId === 'piva') {
          fieldSchema = fieldSchema
            .length(11, t('INVITE_FLOW.pivaLength', 'P.IVA must be 11 digits'))
            .regex(/^\d+$/, t('INVITE_FLOW.numbersOnly', 'Numbers only'));
        } else if (fieldId === 'sdi') {
          fieldSchema = fieldSchema
            .length(7, t('INVITE_FLOW.sdiLength', 'SDI code must be 7 characters'))
            .regex(/^[A-Z0-9]+$/i, t('INVITE_FLOW.alphanumericOnly', 'Alphanumeric only'));
        } else if (fieldId === 'phone') {
          fieldSchema = fieldSchema.max(20, t('INVITE_FLOW.max20Chars', 'Max 20 characters'));
        } else if (fieldId === 'address') {
          fieldSchema = fieldSchema.max(100, t('INVITE_FLOW.max100Chars', 'Max 100 characters'));
        }

        schemaObj[fieldId] = isReq
          ? fieldSchema.min(1, t('INVITE_FLOW.fieldRequired', 'This field is required'))
          : fieldSchema.optional().or(z.literal(''));
      });
    }

    if (step.questions) {
      step.questions.forEach(q => {
        const fieldName = String(q.id);

        if (q.answerType === 'multiple') {
          const arraySchema = z.array(z.string({ invalid_type_error: t('INVITE_FLOW.invalidSelection', 'Invalid selection') }), {
            required_error: t('INVITE_FLOW.selectAtLeastOne', 'Please select at least one option'),
            invalid_type_error: t('INVITE_FLOW.selectAtLeastOne', 'Please select at least one option'),
          });

          schemaObj[fieldName] = q.required
            ? arraySchema.min(1, t('INVITE_FLOW.selectAtLeastOne', 'Please select at least one option'))
            : arraySchema.optional();
        } else {
          const stringSchema = z.string({
            required_error: t('INVITE_FLOW.provideAnswer', 'Please provide an answer'),
            invalid_type_error: t('INVITE_FLOW.provideAnswer', 'Please provide an answer'),
          });

          schemaObj[fieldName] = q.required
            ? z.preprocess(
                val => (val === '' || val === null ? undefined : val),
                stringSchema.min(1, t('INVITE_FLOW.provideAnswer', 'Please provide an answer'))
              )
            : z.preprocess(val => (val === '' || val === null ? undefined : val), stringSchema.optional());
        }
      });
    }

    if (step.type === 'agreement') {
      schemaObj['agreement_accepted'] = z.literal(true, {
        errorMap: () => ({ message: t('INVITE_FLOW.acceptAgreement', 'You must accept the agreement') }),
      });
    }

    if (step.type === 'documents') {
      const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
      const docRequests = getDocumentRequests(step);

      docRequests.forEach((doc, di) => {
        const fileFieldId = `documentFile_${di}`;
        const descFieldId = `documentDescription_${di}`;
        const dateFieldId = `documentExpirationDate_${di}`;

        schemaObj[fileFieldId] = z.any().superRefine((files, ctx) => {
          if (doc.documentRequired && (!files || files.length === 0)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.fileRequired', 'File is required') });
            return;
          }
          if (files && files.length > 0 && files[0].size > MAX_FILE_SIZE) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: t('INVITE_FLOW.fileSizeLimit', 'File size must be less than 3MB') });
          }
        });

        if (doc.includeDescription) {
          const base = z.string({
            required_error: t('INVITE_FLOW.descriptionRequired', 'Description is required'),
            invalid_type_error: t('INVITE_FLOW.descriptionRequired', 'Description is required'),
          });
          schemaObj[descFieldId] = doc.descriptionRequired
            ? base.min(1, t('INVITE_FLOW.descriptionRequired', 'Description is required'))
            : base.optional().or(z.literal(''));
        }

        if (doc.includeExpirationDate) {
          const base = z.string({
            required_error: t('INVITE_FLOW.expirationDateRequired', 'Expiration date is required'),
            invalid_type_error: t('INVITE_FLOW.expirationDateRequired', 'Expiration date is required'),
          });
          schemaObj[dateFieldId] = doc.expirationDateRequired
            ? base.min(1, t('INVITE_FLOW.expirationDateRequired', 'Expiration date is required'))
            : base.optional().or(z.literal(''));
        }
      });
    }
  });

  return z.object(schemaObj);
};
